import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ActionType, AudioAction } from '@textbook-api-client';

import {
  TextbookActionsHandler,
  TextbookActionsHandlerToken,
} from '../../../external/providers/textbook-actions-handler';
import { TextbookActionBase } from '../@base/textbook-action-base.model';
import videojs from 'video.js';
import { Subject, Subscription } from 'rxjs';
import {
  MEDIA_PLAYER_VOLUME_KEY,
  MediaPlayerPlaybackState,
  SourceType,
} from '../@base/components/media-player/media-player.component';
import { debounceTime, filter, first, throttleTime, withLatestFrom } from 'rxjs/operators';
import { TxtbState } from '../../state/states';
import { TextbookMobileDetectToken } from '../../../external/providers/mobile-detect-provider.service';
import * as MobileDetect from 'mobile-detect';
import { GroupInfo } from '../../models/group-info';

export interface AudioState extends TxtbState {
  actionType: ActionType.Audio;
  currentTime: number;
}

@Component({
  selector: 'txtb-audio',
  templateUrl: './audio.component.html',
  styleUrls: ['./audio.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AudioComponent extends TextbookActionBase<AudioAction> implements OnInit, OnDestroy {
  @Input() groupInfo!: GroupInfo;
  subscription = new Subscription();
  audioOptions: videojs.PlayerOptions = {};
  playerLayout = SourceType.Audio;
  updatePlayback$ubject = new Subject<boolean>();
  updateControlsVisibility$ubject = new Subject<boolean>();
  updateCurrentTime$ubject = new Subject<number>();
  updateVolume$ubject = new Subject<number>();
  playerEnded$ubject = new Subject<void>();
  playerCurrentPlaybackState$ubject = new Subject<MediaPlayerPlaybackState>();
  playerCurrentTime$ubject = new Subject<number>();
  playerVolume$ubject = new Subject<number>();
  drawerContentCollapse$ubject = new Subject<boolean>();
  playAfterUncollapse = false;

  constructor(
    @Inject(TextbookActionsHandlerToken) public textbookActionsHandler: TextbookActionsHandler,
    @Inject(TextbookMobileDetectToken) public mobileDetect: MobileDetect
  ) {
    super();
  }

  ngOnInit(): void {
    if (!this.action?.meta?.audioResourceId) {
      return;
    }

    this.initMediaPlayer();

    this.subscription.add(
      this.drawerContentCollapse$ubject
        .pipe(withLatestFrom(this.playerCurrentPlaybackState$ubject))
        .subscribe(([isContentCollapse, currentPlaybackState]) => {
          if (currentPlaybackState === MediaPlayerPlaybackState.Played && !isContentCollapse) {
            this.updatePlayback$ubject.next(false);
            this.playAfterUncollapse = true;
          } else if (
            currentPlaybackState === MediaPlayerPlaybackState.Paused &&
            isContentCollapse &&
            this.playAfterUncollapse
          ) {
            this.updatePlayback$ubject.next(true);
            this.playAfterUncollapse = false;
          }
        })
    );

    this.subscription.add(
      this.playerVolume$ubject
        .pipe(debounceTime(500))
        .subscribe((volume) => localStorage.setItem(MEDIA_PLAYER_VOLUME_KEY, String(volume)))
    );

    this.subscription.add(
      this.playerCurrentTime$ubject.pipe(throttleTime(500)).subscribe((currentTime) =>
        this.textbookActionsHandler.txtbActions$ubject.next({
          actionId: this.action.id,
          state: {
            [this.action.type]: {
              currentTime,
            } as AudioState,
          },
        })
      )
    );

    this.subscription.add(
      this.playerEnded$ubject.subscribe(() => {
        this.textbookActionsHandler.txtbActions$ubject.next({
          actionId: this.action.id,
          state: {
            [this.action.type]: {
              currentTime: 0,
            } as AudioState,
          },
        });
      })
    );
  }

  initMediaPlayer(): void {
    this.textbookActionsHandler
      .getResourceUrl$(this.action?.meta?.audioResourceId)
      .pipe(first())
      .subscribe((audioSrc) => {
        this.audioOptions = {
          autoplay: false,
          controls: true,
          sources: [
            {
              src: audioSrc,
              type: 'audio/mpeg',
            },
          ],
          html5: {
            playsinline: true,
          },
          controlBar: {
            children: [
              {
                name: 'playToggle',
              },
              {
                name: 'CurrentTimeDisplay',
              },
              {
                name: 'DurationDisplay',
              },
              {
                name: 'progressControl',
              },
              {
                name: 'VolumePanel',
              },
            ],
          },
        };
      });
  }

  onMediaCurrentTimeChange(currentTime: number): void {
    this.playerCurrentTime$ubject.next(currentTime);
  }

  onPlayerPlaybackChange(state: MediaPlayerPlaybackState): void {
    this.playerCurrentPlaybackState$ubject.next(state);
  }

  onPlayerVolumeChange(volume: number): void {
    this.playerVolume$ubject.next(volume);
  }

  onPlayerEnded(): void {
    this.playerEnded$ubject.next();
  }

  onPlayerAfterViewInit(): void {
    if (this.isiOS() || this.isSafari() || this.mobileDetect.mobile()) {
      this.updateVolume$ubject.next(1);
    } else {
      const mediaPlayerVolume = localStorage.getItem(MEDIA_PLAYER_VOLUME_KEY) || 1;
      this.updateVolume$ubject.next(Number(mediaPlayerVolume));
    }
  }

  onPlayerLoadedData(): void {
    this.subscription.add(
      this.textbookActionsHandler.overwriteTxtbAction$ubject
        .pipe(filter((value) => value.actionId === this.action.id))
        .subscribe(({ state }) =>
          this.updateCurrentTime$ubject.next(state[this.action.type]?.currentTime || 0)
        )
    );
  }

  controlAudioPlayback(play: boolean): void {
    this.drawerContentCollapse$ubject.next(play);
    this.updateControlsVisibility$ubject.next(play);
  }

  isiOS(): boolean {
    return (
      ['iPad Simulator', 'iPhone Simulator', 'iPod Simulator', 'iPad', 'iPhone', 'iPod'].includes(
        navigator.platform
      ) ||
      // iPad on iOS 13 detection
      (navigator.userAgent.includes('Mac') && 'ontouchend' in document)
    );
  }

  agentHas(keyword: string): boolean {
    return navigator.userAgent.toLowerCase().search(keyword.toLowerCase()) > -1;
  }

  isSafari(): boolean {
    return (
      // @ts-ignore
      (!!window.ApplePaySetupFeature || !!window.safari) &&
      this.agentHas('Safari') &&
      !this.agentHas('Chrome') &&
      !this.agentHas('CriOS')
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
